import React, { useEffect, useState } from "react";
import { fetchTvVideos } from "../services/tvService";
import { timeDifference } from "../utils/timeUtils";
import { SectionHeader } from "./redesign/Sections";

/** Modal with an embedded YouTube player. */
function VideoModal({ video, onClose }) {
  // Close on Escape
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!video) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[900px] overflow-hidden rounded-card bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative aspect-video w-full bg-black">
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerated-encoding; autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="flex items-start gap-3 p-4">
          <div className="min-w-0 flex-1">
            <h3 className="font-head text-[16px] font-bold leading-snug text-ink">
              {video.title}
            </h3>
            <p className="mt-1 text-[12px] text-sub">
              <span className="font-semibold text-brand">{video.channel}</span>
              {video.publishedAt && (
                <> · {timeDifference(video.publishedAt)}</>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-pill bg-chip px-3 py-1.5 font-head text-[12px] font-semibold text-chip-ink hover:bg-ai-bg hover:text-ai"
          >
            Mbyll ×
          </button>
        </div>
      </div>
    </div>
  );
}

function VideoCard({ video, onPlay }) {
  // Embeddable → play in modal. Otherwise (publisher blocked embedding, e.g.
  // Klan) → open the video on YouTube in a new tab.
  const handleClick = () => {
    if (video.embeddable === false) {
      window.open(video.url, "_blank", "noopener,noreferrer");
    } else {
      onPlay(video);
    }
  };

  return (
    <button type="button" onClick={handleClick} className="group text-left">
      <div className="relative aspect-video overflow-hidden rounded-card bg-chip">
        <img
          src={video.thumbnail}
          alt=""
          referrerPolicy="no-referrer"
          className="h-full w-full object-cover transition group-hover:scale-105"
          onError={(e) => {
            e.target.src = "/fallback.jpg";
          }}
        />
        {/* play overlay */}
        <span className="absolute inset-0 grid place-items-center">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-brand/90 text-white shadow-lg transition group-hover:bg-brand">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        </span>
        {/* opens-on-YouTube hint for non-embeddable videos */}
        {video.embeddable === false && (
          <span className="absolute right-2 top-2 rounded-pill bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-white">
            YouTube ↗
          </span>
        )}
      </div>
      <h3 className="mt-2 line-clamp-2 font-head text-[14px] font-bold leading-snug tracking-tight text-ink">
        {video.title}
      </h3>
      <p className="mt-1 text-[11.5px] text-sub">
        <span className="font-head font-semibold text-brand">
          {video.channel}
        </span>
        {video.publishedAt && <> · {timeDifference(video.publishedAt)}</>}
      </p>
    </button>
  );
}

export default function TvPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchTvVideos(40).then((list) => {
      if (cancelled) return;
      setVideos(list);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <SectionHeader title="TV — Lajme video" eyebrow="Aktuale" />

      {loading ? (
        <p className="text-[14px] text-sub">Po ngarkohen videot…</p>
      ) : videos.length === 0 ? (
        <p className="text-[14px] text-sub">
          Nuk ka video për momentin. Provoni më vonë.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-x-5 gap-y-7 sm:grid-cols-2 xl:grid-cols-3">
          {videos.map((v) => (
            <VideoCard key={v.videoId} video={v} onPlay={setActive} />
          ))}
        </div>
      )}

      <VideoModal video={active} onClose={() => setActive(null)} />
    </div>
  );
}
